import {
  API_DATE_FORMAT,
  LeaveRequestStatus,
  UnavailabilityRecordType,
  UnavailabilityStatus,
} from '$constants'
import { ILeaveRequest, IUnavailabilityRecord } from '$models'
import { leaveRequestService, unavailabilityService } from '$services'
import { Dayjs } from 'dayjs'
import { SharedSlice } from 'src/components/slice'
import { StateCreator } from 'zustand'

interface ManageRequestState {
  leaveRequest: ILeaveRequest | null
  unavailabilityRequest: IUnavailabilityRecord | null
  overlappingLeaveRequests: ILeaveRequest[]
  overlappingOneOffUnavailabilityRecords: IUnavailabilityRecord[]
}

export interface ManageRequestSlice extends ManageRequestState {
  /**
   * Gets a Leave Request by its Id.
   * @param id the Id of the Leave Request
   */
  getLeaveRequest: (id: string) => Promise<ILeaveRequest>

  /**
   * Updates the status of a Leave Request.
   * @param leaveRequestId the Id of the Leave Request
   * @param status the status to update the Leave Request to
   */
  updateManageRequestLeaveRequestStatus: (
    leaveRequestId: string,
    status: LeaveRequestStatus
  ) => Promise<ILeaveRequest>

  /**
   * Gets an Unavailability Request by its Id.
   * @param id the Id of the Unavailability Request
   */
  getUnavailabilityRequest: (id: string) => Promise<IUnavailabilityRecord>

  /**
   * Updates the status of an Unavailability Request.
   * @param unavailabilityRequestId the Id of the Unavailability Request
   * @param status the status to update the Unavailability Request to
   */
  updateUnavailabilityRequestStatus: (
    unavailabilityRequestId: string,
    status: UnavailabilityStatus
  ) => Promise<IUnavailabilityRecord>

  /**
   * Gets the overlapping leave and unavailability within a given period
   * @param from the start of the date period
   * @param to the end of the date period
   */
  getOverlappingLeaveAndUnavailability: (
    from: Dayjs,
    to: Dayjs
  ) => Promise<void>
}

const initialState: ManageRequestState = {
  leaveRequest: null,
  unavailabilityRequest: null,
  overlappingLeaveRequests: [],
  overlappingOneOffUnavailabilityRecords: [],
}

const createManageRequestSlice: StateCreator<
  ManageRequestSlice & SharedSlice,
  [],
  [],
  ManageRequestSlice
> = (set) => ({
  ...initialState,

  getLeaveRequest: async (leaveRequestId: string) => {
    const leaveRequest = await leaveRequestService.getById(leaveRequestId)
    set(() => ({ leaveRequest }))
    return leaveRequest
  },

  updateManageRequestLeaveRequestStatus: async (
    leaveRequestId: string,
    status: LeaveRequestStatus
  ) => {
    const updatedLeaveRequest = await leaveRequestService.updateStatus(
      leaveRequestId,
      status
    )
    set(() => ({
      leaveRequest: updatedLeaveRequest,
    }))
    return updatedLeaveRequest
  },

  getUnavailabilityRequest: async (unavailabilityRequestId: string) => {
    const unavailabilityRequest = await unavailabilityService.getById(
      unavailabilityRequestId
    )
    set(() => ({ unavailabilityRequest }))
    return unavailabilityRequest
  },

  updateUnavailabilityRequestStatus: async (
    unavailabilityRequestId: string,
    status: UnavailabilityStatus
  ) => {
    const updatedUnavailabilityRequest =
      await unavailabilityService.updateStatus(unavailabilityRequestId, status)
    set(() => ({
      unavailabilityRequest: updatedUnavailabilityRequest,
    }))
    return updatedUnavailabilityRequest
  },

  getOverlappingLeaveAndUnavailability: async (from: Dayjs, to: Dayjs) => {
    const fromFormatted = from.format(API_DATE_FORMAT)
    // As weekly unavailability can have null as the end date, if to is invalid
    // we can assume this is the case
    const toFormatted = to.isValid() ? to.format(API_DATE_FORMAT) : undefined

    const leaveRequests = leaveRequestService.getAll(
      undefined,
      fromFormatted,
      toFormatted,
      [LeaveRequestStatus.Requested, LeaveRequestStatus.Approved]
    )

    const unavailability = unavailabilityService.getAll(
      undefined,
      fromFormatted,
      toFormatted,
      UnavailabilityRecordType.OneOff,
      [UnavailabilityStatus.Requested, UnavailabilityStatus.Approved]
    )

    const requests = await Promise.all([leaveRequests, unavailability])

    set(() => ({
      overlappingLeaveRequests: requests[0],
      overlappingOneOffUnavailabilityRecords: requests[1],
    }))
  },
})

export default createManageRequestSlice
